import { FloatingFocusManager, FloatingList, autoUpdate, flip, useFloating, useInteractions, useListNavigation, useRole } from '@floating-ui/react';
import { ICONS } from '@frontend/icons';
import { createContext, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import useGlobalSearch from './global-search.controller';
import { CategorySearch } from './searchables/category-search';
import { EquipmentSearch } from './searchables/equipment-search';
import { ProductSearch } from './searchables/product-search';
import { SpotSearch } from './searchables/spot-search';
import { TransactionSearch } from './searchables/transaction-search';
import { UserSearch } from './searchables/user-search';

export interface SelectContextValue {
    getItemProps: ReturnType<typeof useInteractions>['getItemProps'];
}
export const SelectContext = createContext<SelectContextValue>({} as SelectContextValue);

const ID = 'global-search';
export interface GlobalSearchProps {}

const GlobalSearch = (props: GlobalSearchProps) => {
    const intl = useIntl();
    const viewProps = useGlobalSearch(props);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [activeIndex, setActiveIndex] = useState<number | null>(null);

    useEffect(() => {
        const updateListWidth = () => {
            if (containerRef.current) setContainerWidth(containerRef.current.offsetWidth);
        };
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && event.target && !containerRef.current.contains(event.target as Node)) viewProps.changeValue('');
        };

        updateListWidth();
        window.addEventListener('resize', updateListWidth);
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            window.removeEventListener('resize', updateListWidth);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (containerRef.current) setContainerWidth(containerRef.current.offsetWidth);
    }, [containerRef.current]);

    const { refs, floatingStyles, context } = useFloating({
        placement: 'bottom-start',
        whileElementsMounted: autoUpdate,
        middleware: [flip()]
    });

    const elementsRef = useRef<Array<HTMLElement | null>>([]);
    const labelsRef = useRef<Array<string | null>>([]);

    const listNav = useListNavigation(context, {
        listRef: elementsRef,
        allowEscape: false,
        activeIndex,
        onNavigate: setActiveIndex
    });

    const role = useRole(context, { role: 'listbox' });

    const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([listNav, role]);

    const selectContext = useMemo(
        (): SelectContextValue => ({
            getItemProps
        }),
        [getItemProps]
    );

    return (
        <div
            ref={containerRef}
            id={`${ID}-container`}>
            <div
                id={ID}
                className='border-border-default bg-background-surface flex h-full w-full flex-col gap-2 rounded-xl border'>
                <div
                    className='flex h-full w-full items-center'
                    ref={refs.setReference}
                    {...getReferenceProps()}>
                    <ICONS.BUTTON.SEARCH className='text-brand-muted size-9 flex-shrink-0 pl-3' />
                    <input
                        ref={inputRef}
                        id={ID + '-input'}
                        className='form-control bg-background-surface placeholder:text-brand-muted hidden border-0 text-base focus:outline-none lg:flex'
                        value={viewProps.value}
                        type='text'
                        placeholder={intl.formatMessage({ defaultMessage: 'Search', id: 'common.search', description: 'Search placeholder' })}
                        onChange={(e) => viewProps.changeValue(e.target.value)}
                    />
                    <div className='flex items-center justify-center px-3'>
                        <div className='border-brand-muted flex size-5 items-center justify-center rounded-sm border'>
                            <div className='text-brand-muted justify-start text-sm font-medium'>/</div>
                        </div>
                    </div>
                </div>

                <SelectContext.Provider value={selectContext}>
                    {viewProps.showContent && (
                        <FloatingFocusManager
                            context={context}
                            modal={false}>
                            <div
                                className={`border-border-default text-foreground-default bg-background-surface z-[1051] mt-1 flex min-w-64 flex-col rounded-xl border-1 shadow-lg w-[${containerWidth}px]`}
                                ref={refs.setFloating}
                                style={floatingStyles}
                                {...getFloatingProps()}>
                                <FloatingList
                                    elementsRef={elementsRef}
                                    labelsRef={labelsRef}>
                                    <div
                                        className={`no-scrollbar m-0 flex max-h-[50vh] min-w-64 flex-row overflow-x-hidden overflow-y-scroll p-2 w-[${containerWidth}px]`}>
                                        {viewProps.value.length > 0 && (
                                            <div className='w-full'>
                                                {/* <AccountSearch filter={viewProps.filterValue} /> */}
                                                <SpotSearch filter={viewProps.filterValue} />
                                                <UserSearch filter={viewProps.filterValue} />
                                                <ProductSearch filter={viewProps.filterValue} />
                                                <EquipmentSearch filter={viewProps.filterValue} />
                                                <CategorySearch filter={viewProps.filterValue} />
                                                <TransactionSearch filter={viewProps.filterValue} />
                                            </div>
                                        )}
                                    </div>
                                </FloatingList>
                            </div>
                        </FloatingFocusManager>
                    )}
                </SelectContext.Provider>
            </div>
        </div>
    );
};

export default GlobalSearch;
